import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { Document, Page, pdfjs } from 'react-pdf';

// Served from public/ so CRA's webpack never has to process the .mjs bundle.
// Kept in sync with pdfjs-dist by the postinstall script in package.json.
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

/**
 * Fetches a PDF once and exposes it as a same-origin blob: URL.
 * A single request also keeps us off the API's per-minute rate limit, which
 * range requests from a streaming viewer would otherwise eat into.
 */
export const usePdfBlobUrl = (url) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(null);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!url) {
      setBlobUrl(null);
      setError(null);
      return;
    }

    let cancelled = false;
    let objectUrl = null;

    setError(null);

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`Could not load the file (HTTP ${res.status})`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
        setBlobUrl(objectUrl);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, retry]);

  return { blobUrl, error, retry: () => setRetry((n) => n + 1) };
};

/** Best-effort display name for a PDF URL when the API sends no filename. */
export const pdfFileNameFrom = (url) => {
  if (typeof url !== 'string') return 'Document';
  const last = url.split('?')[0].split('/').pop();
  if (!last) return 'Document';
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
};

export const Spinner = ({ label = 'Loading PDF...' }) => {
  const { theme } = useSelector((state) => state.theme);
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16">
      <div className="w-8 h-8 border-4 border-purple-500 rounded-full border-t-transparent animate-spin"></div>
      <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>{label}</p>
    </div>
  );
};

/**
 * Full-document PDF viewer. Pages are drawn to canvas inside a scroll container
 * we own, so scrolling is consistent across browsers and there is no built-in
 * viewer toolbar — and therefore no download or print control.
 */
const PdfViewerModal = ({ url, title, onClose }) => {
  const { theme } = useSelector((state) => state.theme);
  const { blobUrl, error, retry } = usePdfBlobUrl(url);
  const [numPages, setNumPages] = useState(0);
  const [renderError, setRenderError] = useState(null);
  const [pageWidth, setPageWidth] = useState(0);
  const scrollRef = useRef(null);

  const shownError = error || renderError;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  // Pages render to canvas at a fixed pixel width, so track the container size.
  const measure = useCallback(() => {
    const el = scrollRef.current;
    if (el) setPageWidth(Math.max(0, el.clientWidth - 32));
  }, []);

  useLayoutEffect(() => {
    measure();
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [blobUrl, shownError, measure]);

  // react-pdf re-renders the document whenever this prop is not referentially stable.
  const file = useMemo(() => blobUrl, [blobUrl]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 overscroll-contain"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`relative flex flex-col w-full max-w-5xl h-[85vh] rounded-lg shadow-xl overflow-hidden ${
          theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
        }`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pdf-modal-title"
      >
        <div
          className={`flex items-center justify-between gap-3 px-4 py-3 shrink-0 border-b ${
            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
          }`}
        >
          <h2 id="pdf-modal-title" className="text-sm font-semibold truncate">
            {title || 'Document'}
          </h2>
          <div className="flex items-center gap-3 shrink-0">
            {numPages > 0 && (
              <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                {numPages} {numPages === 1 ? 'page' : 'pages'}
              </span>
            )}
            <button
              type="button"
              onClick={onClose}
              className={`px-3 py-1.5 text-sm rounded ${
                theme === 'dark'
                  ? 'bg-gray-700 text-white hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
            >
              Close
            </button>
          </div>
        </div>

        {shownError ? (
          <div className="flex flex-col items-center justify-center flex-1 gap-3 px-6 text-center">
            <p className="text-sm">{shownError}</p>
            <button
              type="button"
              onClick={() => {
                setRenderError(null);
                retry();
              }}
              className="px-4 py-2 text-sm text-white bg-purple-600 rounded hover:bg-purple-700"
            >
              Try again
            </button>
          </div>
        ) : (
          <div
            ref={scrollRef}
            className={`flex-1 min-h-0 p-4 overflow-y-auto select-none overscroll-contain ${
              theme === 'dark' ? 'bg-gray-900' : 'bg-gray-100'
            }`}
            onContextMenu={(e) => e.preventDefault()}
          >
            {file && pageWidth > 0 ? (
              <Document
                file={file}
                onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                onLoadError={(err) => setRenderError(err?.message || 'Could not render this PDF.')}
                loading={<Spinner />}
                error={null}
              >
                {Array.from({ length: numPages }, (_, i) => (
                  <Page
                    key={i}
                    pageNumber={i + 1}
                    width={pageWidth}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    className="mx-auto mb-4 shadow-md last:mb-0"
                    loading={null}
                  />
                ))}
              </Document>
            ) : (
              <Spinner />
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default PdfViewerModal;
